import {
    useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback,
} from 'react';
import ReactDOM from 'react-dom';
import { Badge } from 'reactstrap';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faTruck } from '@fortawesome/free-solid-svg-icons';
import { getDaysSinceWeekStart } from '@s20/common/util/date';
import { useCalendarWeekStartsOn } from '@s20/hooks/tenant.hooks';
import JobCard from './JobCard';
import './CalendarDateHeaderWithPopover.scss';

// Global popover manager to ensure only one popover is open at a time
let activePopoverSetter = null;

// Header content component
const HeaderContent = ({
    date, isToday, isCurrentMonth, jobCount, totalStaff, totalVehicles,
}) => (
    <div className="calendar-date-header d-flex align-items-center text-xs">
        {/* Date Number - Left */}
        <div className="calendar-date-left">
            <time
                dateTime={moment(date).format('YYYY-MM-DD')}
                className={`calendar-date-number font-weight-bold d-flex align-items-center justify-content-center rounded-circle ${
                    isToday ? 'bg-primary text-white' : ''
                } ${!isCurrentMonth ? 'text-muted' : ''}`}
            >
                {moment(date).format('D')}
            </time>
        </div>

        {/* Job Count - Center */}
        <div className="calendar-date-center d-flex align-items-center justify-content-center">
            {jobCount > 0 && (
                <Badge
                    color="light"
                    className="calendar-job-count-badge px-1 py-0"
                >
                    {jobCount} job{jobCount !== 1 ? 's' : ''}
                </Badge>
            )}
        </div>

        {/* Resource Stats - Right */}
        <div className="calendar-date-right d-flex align-items-center">
            {totalStaff > 0 && (
                <div className="calendar-resource-stat d-flex align-items-center">
                    <FontAwesomeIcon
                        icon={faUsers}
                        className="calendar-resource-icon"
                    />
                    <span>{totalStaff}</span>
                </div>
            )}
            {totalVehicles > 0 && (
                <div className="calendar-resource-stat d-flex align-items-center">
                    <FontAwesomeIcon
                        icon={faTruck}
                        className="calendar-resource-icon"
                    />
                    <span>{totalVehicles}</span>
                </div>
            )}
        </div>
    </div>
);

const CalendarDateHeaderWithPopover = ({
    date, events, onJobClick,
}) => {
    const weekStartsOn = useCalendarWeekStartsOn();
    const [showPopover, setShowPopover] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState({});
    const cellRef = useRef(null);
    const popoverRef = useRef(null);
    const timeoutRef = useRef(null);

    // Events are already filtered for this specific date - sort by start time to match calendar display
    const jobsForDay = useMemo(() => (events || []).sort((a, b) => {
        // Sort by start time, then by job ID for consistency
        const timeA = moment(a.start).valueOf();
        const timeB = moment(b.start).valueOf();
        if (timeA !== timeB) return timeA - timeB;
        return (a.resource?.id || 0) - (b.resource?.id || 0);
    }), [events]);

    // Calculate totals using the same logic as JobCard
    // Use API-provided counts first, fall back to counting assignments
    const totalStaff = useMemo(() => jobsForDay.reduce((acc, event) => {
        const job = event.resource || event;
        // Use API-provided staffCount, fallback to counting assignments
        if (job.staffCount !== undefined && job.staffCount !== null) {
            return acc + job.staffCount;
        }
        // Fallback to counting assignments
        const assignments = job.assignments || [];
        const staffAssignments = assignments.filter((a) => a.employeeId);
        return acc + staffAssignments.length;
    }, 0), [jobsForDay]);

    const totalVehicles = useMemo(() => jobsForDay.reduce((acc, event) => {
        const job = event.resource || event;
        // Use API-provided vehicleCount, fallback to counting assignments
        if (job.vehicleCount !== undefined && job.vehicleCount !== null) {
            return acc + job.vehicleCount;
        }
        // Fallback to counting assignments
        const assignments = job.assignments || [];
        const vehicleAssignments = assignments.filter((a) => a.equipmentId);
        return acc + vehicleAssignments.length;
    }, 0), [jobsForDay]);

    const jobCount = jobsForDay.length;

    const isToday = useMemo(() => moment(date).isSame(moment(), 'day'), [date]);
    const isCurrentMonth = useMemo(() => moment(date).isSame(moment(), 'month'), [date]);

    const handleMouseEnter = useCallback(() => {
        clearTimeout(timeoutRef.current);

        // Close any other open popover
        if (activePopoverSetter && activePopoverSetter !== setShowPopover) {
            activePopoverSetter(false);
        }

        // Set this as the active popover
        activePopoverSetter = setShowPopover;
        setShowPopover(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        timeoutRef.current = setTimeout(() => {
            setShowPopover(false);
            // Clear active popover reference if it's this one
            if (activePopoverSetter === setShowPopover) {
                activePopoverSetter = null;
            }
        }, 100); // Small delay to allow moving to popover
    }, []);

    const handlePopoverMouseEnter = useCallback(() => {
        clearTimeout(timeoutRef.current);
    }, []);

    const handlePopoverMouseLeave = useCallback(() => {
        setShowPopover(false);
        // Clear active popover reference if it's this one
        if (activePopoverSetter === setShowPopover) {
            activePopoverSetter = null;
        }
    }, []);

    const handleJobClick = useCallback((event) => {
        if (onJobClick) {
            setShowPopover(false);
            onJobClick(event);
        }
    }, [onJobClick]);

    // Clean up on unmount
    useEffect(() => () => {
            clearTimeout(timeoutRef.current);
            if (activePopoverSetter === setShowPopover) {
                activePopoverSetter = null;
            }
        }, []);

    useLayoutEffect(() => {
        if (showPopover && cellRef.current) {
            // Get the calendar cell dimensions and position
            const cell = cellRef.current.closest('.rbc-date-cell');
            if (cell) {
                const cellRect = cell.getBoundingClientRect();
                const cellWidth = cellRect.width;
                const cellHeight = cellRect.height;

                // Calculate required height for all jobs (33px per job + 20px header + padding)
                const requiredHeight = (jobsForDay.length * 33) + 20 + 4; // 4px for padding (2px header + 2px content)

                // Get viewport height to limit popover height
                const viewportHeight = window.innerHeight;
                const maxPopoverHeight = viewportHeight - cellRect.top - 20; // 20px margin from bottom

                // Width is 120% of cell width
                const popoverWidth = cellWidth * 1.2;

                // Position relative to viewport (fixed positioning)
                const leftPosition = cellRect.left - (cellWidth * 0.1);
                const topPosition = cellRect.top;

                // Use cell height as minimum, but cap at viewport height
                const popoverHeight = Math.min(
                    Math.max(cellHeight, requiredHeight),
                    maxPopoverHeight,
                );

                // Calculate the new style object before updating state
                const newStyle = {
                    width: `${popoverWidth}px`,
                    minHeight: `${cellHeight}px`,
                    maxHeight: `${popoverHeight}px`,
                    left: `${leftPosition}px`,
                    top: `${topPosition}px`,
                };
                setPopoverStyle(newStyle);
            }
        }
    }, [showPopover, jobsForDay.length]);

    return (
        <div ref={cellRef}>
            <div
                className="calendar-popover-trigger"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <HeaderContent
                    date={date}
                    isToday={isToday}
                    isCurrentMonth={isCurrentMonth}
                    jobCount={jobCount}
                    totalStaff={totalStaff}
                    totalVehicles={totalVehicles}
                />
            </div>

            {/* Popover - render using Portal to escape any z-index/overflow issues */}
            {showPopover && jobCount > 0 && ReactDOM.createPortal(
                <div
                    ref={popoverRef}
                    className={`calendar-popover ${!showPopover ? 'popover-hidden' : ''}`}
                    style={popoverStyle}
                    onMouseEnter={handlePopoverMouseEnter}
                    onMouseLeave={handlePopoverMouseLeave}
                >
                    {/* Sticky header */}
                    <div className="calendar-popover-header">
                        <HeaderContent
                            date={date}
                            isToday={isToday}
                            isCurrentMonth={isCurrentMonth}
                            jobCount={jobCount}
                            totalStaff={totalStaff}
                            totalVehicles={totalVehicles}
                        />
                    </div>

                    {/* Job Cards with padding */}
                    <div className="calendar-popover-content">
                        {jobsForDay.map((event, idx) => {
                            // The first column of the tenant week gets its chip on the right
                            const chipPosition = getDaysSinceWeekStart(date, weekStartsOn) === 0 ? 'right' : 'left';

                            // Check if this is a previous day
                            const today = moment().startOf('day');
                            const jobDate = moment(date).startOf('day');
                            const isPreviousDay = jobDate.isBefore(today);

                            return (
                                <JobCard
                                    key={event.resource.id || idx}
                                    job={event.resource}
                                    onClick={() => handleJobClick(event)}
                                    chipPosition={chipPosition}
                                    isPreviousDay={isPreviousDay}
                                    inPopover
                                    currentView="month"
                                />
                            );
                        })}
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default CalendarDateHeaderWithPopover;